import { Component, type ErrorInfo, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SafeToastViewport } from "@/lib/safeToast";
import { AuthProvider, useAuth } from "@/lib/auth";
import { AppShell } from "@/components/layout/AppShell";
import AuthPage from "./pages/Auth";
import ResetPasswordPage from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import MySpaceToday from "./pages/MySpaceToday";
import Journal from "./pages/Journal";
import JournalDay from "./pages/JournalDay";
import Patients from "./pages/Patients";
import PatientEdit from "./pages/PatientEdit";
import PatientCockpit from "./pages/PatientCockpit";
import Sessions from "./pages/Sessions";
import SessionView from "./pages/SessionView";
import Medications from "./pages/Medications";
import MedicationInventory from "./pages/MedicationInventory";
import PatientDiary from "./pages/PatientDiary";
import PharmacologyKnowledge from "./pages/PharmacologyKnowledge";
import Library from "./pages/Library";
import ToolsHub from "./pages/ToolsHub";
import InsightsHub from "./pages/InsightsHub";
import ClinicalIntelligence from "./pages/ClinicalIntelligence";
import Doses from "./pages/Doses";
import SubstanceUse from "./pages/SubstanceUse";
import Curves from "./pages/Curves";
import Symptoms from "./pages/Symptoms";
import Interactions from "./pages/Interactions";
import Reports from "./pages/Reports";
import PharmacologyWorkbench from "./pages/PharmacologyWorkbench";
import LaboratorioPKPD from "./pages/LaboratorioPKPD";
import SettingsPage from "./pages/Settings";
import Audit from "./pages/Audit";
import SystemDiagnostics from "./pages/SystemDiagnostics";
import ImportPsyLog from "./pages/ImportPsyLog";
import BackupPage from "./pages/Backup";
import NotFound from "./pages/NotFound";
import { PRIMARY_PATIENT_ID } from "@/lib/clinical/singlePatientMode";

class AppErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) { console.error("ClinicalOS runtime error", error, info); }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-[100dvh] bg-background text-foreground grid place-items-center p-6">
        <div className="w-full max-w-lg rounded-2xl border bg-card p-6 shadow-xl">
          <div className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">ClinicalOS</div>
          <h1 className="mt-2 text-xl font-semibold">Falha ao iniciar o aplicativo</h1>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">O aplicativo encontrou um erro de execução. Tente recarregar esta janela.</p>
          <button type="button" className="mt-5 h-11 w-full rounded-xl bg-primary px-4 text-sm font-medium text-primary-foreground" onClick={() => window.location.reload()}>Tentar novamente</button>
          <details className="mt-4 text-xs text-muted-foreground"><summary>Detalhes técnicos</summary><pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap">{this.state.error.message}</pre></details>
        </div>
      </div>
    );
  }
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false } },
});

function Protected({ children }: { children: JSX.Element }) {
  const { session, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-[100dvh] grid place-items-center text-sm text-muted-foreground">Carregando…</div>;
  if (!session) {
    return (
      <Navigate
        to="/auth"
        replace
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }
  return <AppShell>{children}</AppShell>;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <SafeToastViewport />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/auth/redefinir-senha" element={<ResetPasswordPage />} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/meu-espaco" element={<Protected><MySpaceToday /></Protected>} />
            <Route path="/journal" element={<Protected><Journal /></Protected>} />
            <Route path="/journal/:date" element={<Protected><JournalDay /></Protected>} />
            <Route path="/biblioteca" element={<Protected><Library /></Protected>} />
            <Route path="/ferramentas" element={<Protected><ToolsHub /></Protected>} />
            <Route path="/insights" element={<Protected><InsightsHub /></Protected>} />
            <Route path="/pacientes" element={<Protected><Navigate to={`/pacientes/${PRIMARY_PATIENT_ID}`} replace /></Protected>} />
            <Route path="/pacientes/novo" element={<Protected><Navigate to={`/pacientes/${PRIMARY_PATIENT_ID}`} replace /></Protected>} />
            <Route path="/pacientes/:id" element={<Protected><PatientCockpit /></Protected>} />
            <Route path="/pacientes/:id/editar" element={<Protected><PatientEdit /></Protected>} />
            <Route path="/sessoes" element={<Protected><Sessions /></Protected>} />
            <Route path="/sessoes/:id" element={<Protected><SessionView /></Protected>} />
            <Route path="/medicamentos" element={<Protected><Medications /></Protected>} />
            <Route path="/diario-paciente" element={<Protected><PatientDiary /></Protected>} />
            <Route path="/estoque-medicamentos" element={<Protected><MedicationInventory /></Protected>} />
            <Route path="/doses" element={<Protected><Doses /></Protected>} />
            <Route path="/uso-substancias" element={<Protected><SubstanceUse /></Protected>} />
            <Route path="/curvas" element={<Protected><Curves /></Protected>} />
            <Route path="/inteligencia-clinica" element={<Protected><ClinicalIntelligence /></Protected>} />
            <Route path="/sintomas" element={<Protected><Symptoms /></Protected>} />
            <Route path="/interacoes" element={<Protected><Interactions /></Protected>} />
            <Route path="/relatorios" element={<Protected><Reports /></Protected>} />
            <Route path="/auditoria" element={<Protected><Audit /></Protected>} />
            <Route path="/laboratorio-pkpd" element={<Protected><LaboratorioPKPD /></Protected>} />
            <Route path="/base-farmacologica" element={<Protected><LaboratorioPKPD /></Protected>} />
            <Route path="/base-pkpd" element={<Protected><LaboratorioPKPD /></Protected>} />
            <Route path="/base-farmacologica/workbench" element={<Protected><PharmacologyWorkbench /></Protected>} />
            <Route path="/conhecimento-farmacologico" element={<Protected><PharmacologyKnowledge /></Protected>} />
            <Route path="/diagnostico-sistema" element={<Protected><SystemDiagnostics /></Protected>} />
            <Route path="/configuracoes" element={<Protected><SettingsPage /></Protected>} />
            <Route path="/importar" element={<Protected><ImportPsyLog /></Protected>} />
            <Route path="/backup" element={<Protected><BackupPage /></Protected>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
